import { useState } from 'react';
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  Minus,
  Pencil,
  Plus,
  Tag,
  Trash2,
  Truck
} from 'lucide-react';
import type { InventoryItemCategory } from '../Types';
import { cn } from '@/shared/utils/helpers';
import { onboardingStepShellClass } from '../Utils/onboardingLayout';
import type { ListedInventoryItem } from './AddItemsForm';

const getDisplayStockUnit = (unit: string): string => {
  const trimmed = unit.trim();
  return /^val(?:ue)?(?:\s|$)/i.test(trimmed) ? '' : trimmed;
};

interface ReviewStepProps {
  items: ListedInventoryItem[];
  categories: InventoryItemCategory[];
  assigningId?: string | null;
  onAssignCategory: (
    item: ListedInventoryItem,
    category: InventoryItemCategory
  ) => Promise<void>;
  onRequestAddCategory?: () => void;
  onNext: () => void;
  onPrev: () => void;
}

export const ReviewStep = ({
  items,
  categories,
  assigningId = null,
  onAssignCategory,
  onRequestAddCategory,
  onNext,
  onPrev
}: ReviewStepProps) => {
  const needsAttention = items.filter(i => !i.categoryName?.trim());
  const goodToGo = items.filter(i => Boolean(i.categoryName?.trim()));
  const [needsOpen, setNeedsOpen] = useState(true);
  // Collapse the long “good” list by default when there is work remaining.
  const [goodOpen, setGoodOpen] = useState(() => needsAttention.length === 0);

  const renderItemCard = (
    item: ListedInventoryItem,
    options: { needsCategory: boolean }
  ) => {
    const quantity = Math.max(0, Number(item.quantityOnHand ?? 0));
    const displayStockUnit = getDisplayStockUnit(item.stockUnit);
    const hasSupplier = Boolean(item.supplierName?.trim());
    const hasCategory = Boolean(item.categoryName?.trim());
    const isAssigning = assigningId === item.localId;

    return (
      <article
        key={item.localId}
        className={cn(
          'rounded-lg border bg-white p-3 shadow-sm',
          options.needsCategory
            ? 'border-amber-200 bg-amber-50/40'
            : 'border-[#e7e9ee]'
        )}
      >
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-sm font-semibold text-[#171717]">
              {item.name}
            </h3>
            <p className="mt-0.5 text-[11px] text-[#6f747c]">
              Min: {item.parLevel ?? 0}
              {displayStockUnit ? ` ${displayStockUnit}` : ''}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span
                className={cn(
                  'inline-flex items-center gap-1 text-[11px] font-semibold',
                  hasCategory ? 'text-[#087f8c]' : 'text-amber-700'
                )}
              >
                <Tag className="h-3 w-3" />
                {hasCategory ? item.categoryName : 'No category'}
                {item.categorySuggested && hasCategory ? (
                  <span className="ml-0.5 rounded bg-[#087f8c]/10 px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#087f8c]">
                    AI
                  </span>
                ) : null}
              </span>
              <span
                className={cn(
                  'inline-flex items-center gap-1 text-[11px] font-semibold',
                  hasSupplier ? 'text-[#087f8c]' : 'text-[#d16b68]'
                )}
              >
                <Truck className="h-3 w-3" />
                {hasSupplier ? item.supplierName : 'No supplier'}
              </span>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1">
            <span title="Edit quantity in Items step" className="inline-flex">
              <button
                type="button"
                disabled
                className="pointer-events-none grid h-8 w-8 place-items-center rounded-md bg-[#087f8c] text-white opacity-40"
                aria-label={`Decrease ${item.name}`}
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
            </span>
            <div
              title={`${quantity}${displayStockUnit ? ` ${displayStockUnit}` : ''}`}
              className="flex h-8 w-[78px] shrink-0 items-center justify-center gap-1 overflow-hidden rounded-md border border-[#dfe3e7] bg-white px-1.5 text-xs font-bold tabular-nums text-[#087f8c]"
            >
              <span className="shrink-0">{quantity}</span>
              {displayStockUnit && (
                <span className="min-w-0 truncate font-medium text-[#6f747c]">
                  {displayStockUnit}
                </span>
              )}
            </div>
            <span title="Edit quantity in Items step" className="inline-flex">
              <button
                type="button"
                disabled
                className="pointer-events-none grid h-8 w-8 place-items-center rounded-md bg-[#087f8c] text-white opacity-40"
                aria-label={`Increase ${item.name}`}
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </span>
            <div className="ml-0.5 flex flex-col gap-1">
              <span title="Edit in Items step" className="inline-flex">
                <button
                  type="button"
                  disabled
                  className="pointer-events-none grid h-7 w-7 place-items-center rounded-md border border-[#e7e9ee] bg-white text-[#9a9fa6] opacity-40"
                  aria-label={`Edit ${item.name}`}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              </span>
              <span title="Remove in Items step" className="inline-flex">
                <button
                  type="button"
                  disabled
                  className="pointer-events-none grid h-7 w-7 place-items-center rounded-md border border-[#e7e9ee] bg-white text-[#9a9fa6] opacity-40"
                  aria-label={`Remove ${item.name}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </span>
            </div>
          </div>
        </div>

        {options.needsCategory && (
          <div className="mt-3 border-t border-amber-200/80 pt-3">
            {categories.length === 0 ? (
              <div className="space-y-2">
                <p className="text-xs text-[#6f747c]">
                  No categories yet. Create one to assign it here.
                </p>
                {onRequestAddCategory && (
                  <button
                    type="button"
                    onClick={onRequestAddCategory}
                    className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-[#087f8c]/40 bg-white px-2.5 py-1.5 text-xs font-semibold text-[#087f8c] transition hover:border-[#087f8c] hover:bg-[#087f8c]/5"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add category
                  </button>
                )}
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {categories.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    disabled={isAssigning}
                    onClick={() => {
                      void onAssignCategory(item, c);
                    }}
                    className="rounded border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium transition-colors hover:border-amber-500 hover:bg-amber-50 disabled:opacity-50"
                  >
                    {isAssigning ? (
                      <Loader2 className="inline h-3 w-3 animate-spin" />
                    ) : (
                      c.name
                    )}
                  </button>
                ))}
                {onRequestAddCategory && (
                  <button
                    type="button"
                    onClick={onRequestAddCategory}
                    className="rounded border border-dashed border-[#087f8c]/35 bg-white px-2.5 py-1 text-xs font-semibold text-[#087f8c] transition hover:border-[#087f8c] hover:bg-[#087f8c]/5"
                  >
                    + New
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </article>
    );
  };

  return (
    <div className={onboardingStepShellClass}>
      <div>
        <h2 className="text-2xl font-semibold tracking-tight text-[#171717]">
          Review
        </h2>
        <p className="mt-1 text-sm text-[#6f747c]">
          {items.length === 0
            ? 'No items yet. Go back and add some first.'
            : needsAttention.length === 0
              ? 'Everything looks good.'
              : `${needsAttention.length} item${needsAttention.length !== 1 ? 's' : ''} missing a category.`}
        </p>
      </div>

      <section className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-[#e7e9ee] bg-white shadow-sm lg:h-[min(70vh,640px)]">
        <div className="shrink-0 border-b border-[#e7e9ee] px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-[#171717]">Your items</h3>
            <span className="text-[11px] text-[#8a8f96]">
              {needsAttention.length > 0
                ? `${needsAttention.length} need category`
                : `${items.length} ready`}
            </span>
          </div>
          <p className="mt-0.5 text-[11px] text-[#8a8f96]">
            Confirm categories, then continue.
          </p>
        </div>

        <div className="min-h-0 max-h-[min(68vh,620px)] flex-1 space-y-3 overflow-y-auto bg-[#f7f8f9] p-3 lg:max-h-none">
          {items.length === 0 ? (
            <div className="rounded-md border border-dashed border-[#dfe3e7] bg-white px-4 py-8 text-center text-sm text-[#6f747c]">
              No items to review yet.
            </div>
          ) : (
            <>
              {needsAttention.length > 0 && (
                <div className="overflow-hidden rounded-lg border border-amber-200 bg-white">
                  <div className="flex items-center gap-2 border-b border-amber-100 bg-amber-50/70 px-3 py-2">
                    <button
                      type="button"
                      onClick={() => setNeedsOpen(open => !open)}
                      className="flex min-w-0 flex-1 items-center gap-2 text-left text-amber-700 transition hover:text-amber-800"
                      aria-expanded={needsOpen}
                    >
                      {needsOpen ? (
                        <ChevronDown className="h-4 w-4 shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 shrink-0" />
                      )}
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      <span className="truncate text-sm font-semibold">
                        Needs category
                      </span>
                      <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">
                        {needsAttention.length}
                      </span>
                    </button>
                    {onRequestAddCategory && (
                      <button
                        type="button"
                        onClick={onRequestAddCategory}
                        className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-[#087f8c]/30 bg-white px-2.5 text-xs font-bold text-[#087f8c] transition hover:bg-[#087f8c]/5"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Add category
                      </button>
                    )}
                  </div>
                  {needsOpen && (
                    <div className="space-y-2 bg-[#f7f8f9] p-2">
                      {needsAttention.map(item =>
                        renderItemCard(item, { needsCategory: true })
                      )}
                    </div>
                  )}
                </div>
              )}

              {goodToGo.length > 0 && (
                <div className="overflow-hidden rounded-lg border border-[#e7e9ee] bg-white">
                  <button
                    type="button"
                    onClick={() => setGoodOpen(open => !open)}
                    className="flex w-full items-center gap-2 border-b border-[#e7e9ee] bg-white px-3 py-2.5 text-left text-[#087f8c] transition hover:bg-[#087f8c]/[0.04]"
                    aria-expanded={goodOpen}
                  >
                    {goodOpen ? (
                      <ChevronDown className="h-4 w-4 shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0" />
                    )}
                    <Check className="h-4 w-4 shrink-0" />
                    <span className="truncate text-sm font-semibold">
                      Good to go
                    </span>
                    <span className="rounded-full bg-[#087f8c]/10 px-1.5 py-0.5 text-[10px] font-bold text-[#087f8c]">
                      {goodToGo.length}
                    </span>
                  </button>
                  {goodOpen && (
                    <div className="space-y-2 bg-[#f7f8f9] p-2">
                      {goodToGo.map(item =>
                        renderItemCard(item, { needsCategory: false })
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <div className="flex items-center gap-3 border-t border-[#e7e9ee] pt-4">
        <button
          type="button"
          onClick={onPrev}
          className="rounded-md px-3 py-2 text-sm font-medium text-[#6f747c] transition hover:bg-[#f0f2f5] hover:text-[#171717]"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onNext}
          className="ml-auto rounded-md bg-[#087f8c] px-3 py-2 text-sm font-semibold text-white transition hover:bg-[#066873]"
        >
          Continue
        </button>
      </div>
    </div>
  );
};
